import { cn, media } from '@rst/libs-ts'
import React, { MouseEvent, useLayoutEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
//@ts-ignore
import Div100vh from 'react-div-100vh'
import {
	disableBodyScroll,
	enableBodyScroll,
	clearAllBodyScrollLocks,
} from 'body-scroll-lock-upgrade'
import { createEvent, createStore } from 'effector'
import { CSSTransition } from 'react-transition-group'
import { useModelCtx } from '../model/z-index'
import { useStore } from 'effector-react'
import { ZIndexHandler } from './z-index-handle'
import { scrollbarCss } from '../../molecules'
import { color } from '../../atoms'

interface ScrollPropsType extends React.HTMLAttributes<HTMLDivElement> {
	close: (e: MouseEvent) => void
	fixedReactComponent?: any
	active?: boolean
	modalType?: 'defalut' | 'right-side' | 'full'
	bgNoColor?: boolean
	shouldCloseBg?: boolean
	preventDefault?: boolean
}

const $countsModal = createStore(0)
const increment = createEvent()
const decrement = createEvent()

$countsModal.on(increment, x => x + 1)
$countsModal.on(decrement, x => x - 1)

$countsModal.updates.watch(x => {
	if (x === 0) clearAllBodyScrollLocks()
})

const useScrollLock = active => {
	const ref = useRef<HTMLDivElement>()
	useLayoutEffect(() => {
		const findGridView = document.getElementsByClassName('rst-grid-view')
		if (findGridView[0]) return () => {}
		if (active && ref?.current) {
			disableBodyScroll(ref.current, { reserveScrollBarGap: true })
			increment()
		}
		if (!active) {
			if (ref.current) {
				enableBodyScroll(ref.current)
			}
			decrement()
		}
		return () => {
			if (ref?.current) {
				if (ref.current) {
					enableBodyScroll(ref.current)
				}
				decrement()
			}
		}
	}, [ref, active])
	return ref
}

export const Scroll: React.FC<ScrollPropsType> = ({
	children,
	fixedReactComponent,
	close,
	className,
	bgNoColor,
	shouldCloseBg,
	...props
}) => {
	const ref = useScrollLock(props.active)
	const { $zIndex } = useModelCtx()
	const zIndex = useStore($zIndex)

	return (
		<CSSTransition
			in={props.active}
			timeout={250}
			classNames={'z-index'}
			unmountOnExit
		>
			<DivHeight
				preventDefault={props.preventDefault}
				className={className}
				onScroll={undefined}
				zindex={zIndex || 1000}
			>
				{props.active && props.id ? <ZIndexHandler id={props.id} /> : null}
				<PageScrollStyled
					modalType={props.modalType}
					ref={ref}
					id={'modal-page-scroll'}
					className={cn('modal-page-scroll', className)}
					{...props}
				>
					{props.preventDefault ? null : (
						<CSSTransition
							in={props.active}
							timeout={250}
							classNames={'__modal-overlay-animation'}
							unmountOnExit
						>
							<Bg bgNoColor={bgNoColor}>
								{typeof fixedReactComponent === 'function' &&
									fixedReactComponent?.(close)}
								<OutClickWrapper>
									<OutClick
										onClick={
											props.modalType === 'right-side'
												? shouldCloseBg
													? close
													: null
												: close
										}
										className="modal-page-out-side-click"
									/>
								</OutClickWrapper>
							</Bg>
						</CSSTransition>
					)}
					<CSSTransition
						in={props.active}
						timeout={250}
						classNames={'__modal-animation'}
						unmountOnExit
					>
						{children}
					</CSSTransition>
				</PageScrollStyled>
			</DivHeight>
		</CSSTransition>
	)
}

const DivHeight = styled(Div100vh)<{
	zindex: number
	preventDefault?: boolean
}>`
	position: fixed;
	left: 0;
	top: 0;
	z-index: ${({ zindex }) => zindex};
	width: 100vw;
	overflow: hidden;

	${({ preventDefault }) =>
		preventDefault &&
		css`
			pointer-events: none;
		`}
`

const OutClickWrapper = styled.div`
	width: 100%;
	height: 0;
	position: sticky;
	top: 0;
`

const OutClick = styled(Div100vh)`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
`

const Bg = styled.div<{ bgNoColor?: boolean }>`
	display: flex;
	flex-flow: column;
	width: 100%;

	${media.pure.less(media.size.sm)} {
		justify-content: space-between;
	}
	&:before,
	&:after {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: -1;
	}
	&:before {
		content: '';
		background: linear-gradient(
				107.44deg,
				#32bff3 0%,
				rgba(100, 208, 248, 0) 99.16%
			),
			linear-gradient(
				171.41deg,
				#f46de1 11.56%,
				rgba(243, 110, 225, 0) 70.9%,
				rgba(243, 110, 225, 0) 70.9%
			),
			linear-gradient(
				275.54deg,
				#f9d0b9 0.91%,
				rgba(255, 255, 255, 0.08) 81.46%
			),
			linear-gradient(331.15deg, #f46ce1 27.74%, rgba(180, 167, 237, 0) 57.92%),
			linear-gradient(252.44deg, #c374fa 0%, #917bea 100%);
		opacity: 0.8;
	}
	&:after {
		content: '';
		background: rgb(13, 13, 13, 0.8);
	}

	${({ bgNoColor }) =>
		bgNoColor &&
		css`
			&:before {
				background: transparent;
			}
			&:after {
				background: transparent;
			}
		`}
`

const PageScrollStyled = styled.div<{
	modalType?: 'defalut' | 'right-side' | 'full'
}>`
	overflow-y: auto;
	height: 100%;
	display: flex;
	flex-flow: column;
	width: 100%;

	${media.pure.less(media.size.sm)} {
		justify-content: space-between;
	}

	${media.pure.greater(media.size.sm)} {
		${scrollbarCss}
	}

	${({ modalType }) =>
		modalType === 'right-side' &&
		css`
			align-items: flex-end;
			&::-webkit-scrollbar {
				width: 5px;
			}

			&::-webkit-scrollbar-track {
				background: transparent;
			}

			&::-webkit-scrollbar-thumb {
				background: ${color('gray-11')};
				border-radius: 10px;
			}

			.awesome-modal-row {
				max-height: 100%;
				margin: 0px;
				margin-left: 0px !important;
				margin-right: 0px !important;
			}

			.awesome-modal-col {
				padding-left: 0px !important;
				padding-right: 0px !important;
			}

			.awesome-modal-body {
				height: 100%;
				border-radius: 0px !important;
			}
		`}
`
